/**
 * Inline SVG icons for the left nav rail (Phase 18).
 *
 * Inline rather than an icon font or sprite sheet: there are twelve of them,
 * they never change, and a separate request or dependency for ~2KB of paths
 * would cost more than it saves. All share one 24×24 viewBox and inherit
 * `currentColor` so the active/hover states need no icon-specific CSS.
 */

import type { ProjectSectionKey, GlobalKey } from '@/views/app-shell';

const svg = (paths: string): string =>
  `<svg class="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"
        aria-hidden="true">${paths}</svg>`;

const ICONS: Record<ProjectSectionKey | GlobalKey, string> = {
  settings:      svg('<circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.6 1.6 0 00.3 1.8l.1.1a2 2 0 11-2.8 2.8l-.1-.1a1.6 1.6 0 00-1.8-.3 1.6 1.6 0 00-1 1.5V21a2 2 0 11-4 0v-.1A1.6 1.6 0 007 19.4a1.6 1.6 0 00-1.8.3l-.1.1a2 2 0 11-2.8-2.8l.1-.1a1.6 1.6 0 00.3-1.8 1.6 1.6 0 00-1.5-1H1a2 2 0 110-4h.1A1.6 1.6 0 002.6 9a1.6 1.6 0 00-.3-1.8l-.1-.1a2 2 0 112.8-2.8l.1.1a1.6 1.6 0 001.8.3H7a1.6 1.6 0 001-1.5V3a2 2 0 114 0v.1a1.6 1.6 0 001 1.5 1.6 1.6 0 001.8-.3l.1-.1a2 2 0 112.8 2.8l-.1.1a1.6 1.6 0 00-.3 1.8V9a1.6 1.6 0 001.5 1H21a2 2 0 110 4h-.1a1.6 1.6 0 00-1.5 1z"/>'),
  projects:      svg('<path d="M4 6h6l2 2h8v10H4z"/>'),
  // A stack of swatches: the firm's product library.
  library:       svg('<rect x="4" y="4" width="6" height="16" rx="1"/><rect x="11" y="4" width="4" height="16" rx="1"/><path d="M16.5 5.2l3.3-.9 3 14.5-3.3.9z"/>'),
  dashboard:     svg('<path d="M4 13h7V4H4zM13 20h7V4h-7zM4 20h7v-5H4z"/>'),
  rooms:         svg('<path d="M3 12l9-8 9 8M5 10v10h14V10"/>'),
  exterior:      svg('<path d="M3 20h18M6 20V9l6-5 6 5v11M10 20v-5h4v5"/>'),
  schedule:      svg('<rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 10h18M8 3v4M16 3v4"/>'),
  // A checklist with a tick: what's left to fix.
  punch:         svg('<path d="M4 7l2 2 3-3M4 15l2 2 3-3"/><path d="M12 8h8M12 16h8"/>'),
  budget:        svg('<path d="M12 3v18M16 7H10a2.5 2.5 0 000 5h4a2.5 2.5 0 010 5H7"/>'),
  specs:         svg('<path d="M4 7h16M4 12h16M4 17h10"/><circle cx="19" cy="17" r="2"/>'),
  files:         svg('<path d="M6 3h8l4 4v14H6z"/><path d="M14 3v5h4"/>'),
  contacts:      svg('<circle cx="9" cy="9" r="3"/><path d="M4 20c0-3 2.2-5 5-5s5 2 5 5"/><path d="M16 8h5M16 12h5M16 16h3"/>'),
  notes:         svg('<path d="M5 4h10l4 4v12H5z"/><path d="M8 11h8M8 15h5"/>'),
  change_orders: svg('<path d="M4 7h11M4 7l3-3M4 7l3 3"/><path d="M20 17H9M20 17l-3-3M20 17l-3 3"/>'),
  questionnaire: svg('<circle cx="12" cy="12" r="9"/><path d="M9.5 9.5a2.6 2.6 0 015 .8c0 1.8-2.5 2-2.5 3.7"/><circle cx="12" cy="17.5" r=".6" fill="currentColor"/>'),
  export:        svg('<path d="M12 3v11m0 0l-4-4m4 4l4-4"/><path d="M5 17v3h14v-3"/>'),
};

/** Icon markup for a nav key. Falls back to a neutral dot for unknown keys. */
export function navIcon(key: ProjectSectionKey | GlobalKey): string {
  return ICONS[key] ?? svg('<circle cx="12" cy="12" r="3"/>');
}

/** Every key that has an icon — used by the test that guards coverage. */
export function iconKeys(): string[] {
  return Object.keys(ICONS);
}
